import { IPage } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { IAppClient } from './app'
import { ViewComponent } from './component'
import { AppLayer, IAppLayer } from './layer'

export interface IAppPage extends IAppLayer {
  settings: IPage
  /**
   * Toogle page visibility
   */
  ToggleVisibility(isVisible: boolean): Promise<void>
  /**
   * Make page private
   */
  MakePrivate(isPrivate: boolean): Promise<void>
  /**
   * Inject globals into the page
   */
  AttachGlobals(): Promise<void>
  /**
   * Detach globals from the page
   */
  DetachGlobals(): Promise<void>
}

export class AppPage extends AppLayer implements IAppPage {
  settings: IPage

  constructor(appClient: IAppClient, index: number, page: IPage) {
    super(appClient, index, page, 'page')
    this.settings = page
  }

  //--------------------------------------------------------------------------------------//
  //                             Page Specific Methods                                    //
  //--------------------------------------------------------------------------------------//

  async MakePrivate(isPrivate: boolean): Promise<void> {
    this.settings._Private = isPrivate
    this.notifySubscribers()
    await this.provider.makePrivate(this.appID, this.settings._id, isPrivate)
  }

  async ToggleVisibility(isVisible: boolean): Promise<void> {
    this.settings._IsVisible = isVisible // Update the setting on the correct page
    this.notifySubscribers()
    await this.provider.toggleVisibility(this.appID, this.settings._id, isVisible)
  }

  async AttachGlobals(): Promise<void> {
    if (!this.appClient.builderSettings || !this.settings._BodySettings) return

    // inject global element headers and footers into the page
    const globals = this.appClient.builderSettings._Globals
    const globalsBody = globals._Body
    const globalsSettings = globals._BodySettings
    if (!globalsBody.length) return

    const headerBodies = globalsBody.filter((b) => b._ComponentID === 'header' && b._GroupID === 'globals')
    const footerBodies = globalsBody.filter((b) => b._ComponentID === 'footer' && b._GroupID === 'globals')

    const inject = (bodies: any[], parentId?: string) => {
      for (const body of bodies) {
        // copy settings
        this.settings._BodySettings = {
          ...this.settings._BodySettings,
          [body._id]: globalsSettings[body._id],
        }

        // register component
        this.componentsMap.set(body._id, new ViewComponent(this, body, parentId))
        if (Array.isArray(body._Body) && body._Body.length > 0) {
          inject(body._Body, body._id)
        }
      }
    }

    inject(headerBodies)
    inject(footerBodies)

    this.notifySubscribers()
  }

  async DetachGlobals(): Promise<void> {
    if (!this.appClient.builderSettings || !this.settings._BodySettings) return

    // detach global element headers and footers from the page
    const globals = this.appClient.builderSettings._Globals
    const globalsBody = globals._Body
    if (!globalsBody.length) return

    const headerBodies = globalsBody.filter((b) => b._ComponentID === 'header' && b._GroupID === 'globals')
    const footerBodies = globalsBody.filter((b) => b._ComponentID === 'footer' && b._GroupID === 'globals')

    const detach = (bodies: any[]) => {
      for (const body of bodies) {
        this.componentsMap.delete(body._id)
        delete this.settings._BodySettings![body._id]

        if (Array.isArray(body._Body) && body._Body.length > 0) {
          detach(body._Body)
        }
      }
    }

    detach(headerBodies)
    detach(footerBodies)

    this.notifySubscribers()
  }
}
